import { useSyncExternalStore } from 'react'

/**
 * Subscribes to a CSS media query and returns the current match
 * boolean. Re-renders on media-query change events.
 *
 * Uses `useSyncExternalStore` — the canonical React hook for browser
 * subscriptions — so it's tear-free under concurrent rendering and
 * doesn't trip the `react-hooks/set-state-in-effect` lint.
 *
 * Used by `ProjectRoute` and `LibraryRoute` to derive the mobile
 * read-only flag (PRD #73, #74). The SSR snapshot returns `false`
 * (desktop default); the first client render reconciles with the
 * actual `matchMedia` value.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {}
      }
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    },
    () => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
      return window.matchMedia(query).matches
    },
    () => false,
  )
}
